import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * FeatureFlagContext — memuat effective features tenant primary sekali,
 * lalu meng-share ke seluruh komponen. Sumber: GET /api/features/current
 * (public, single-tenant). Multi-tenant di masa depan tinggal ganti sumber ini.
 *
 * State:
 *   loading    : true saat pertama fetch
 *   error      : string kalau gagal
 *   package    : string package tenant ("STARTER" | "GROWTH" | "ENTERPRISE")
 *   features   : { [feature_key]: { enabled, source, ... } }
 *   enabledKeys: string[] — daftar feature_key yang enabled
 *   refresh()  : re-fetch effective features
 */
const FeatureFlagContext = createContext({
  loading: true,
  error: null,
  package: null,
  features: {},
  enabledKeys: [],
  refresh: async () => {},
});

export function FeatureFlagProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    error: null,
    package: null,
    features: {},
    enabledKeys: [],
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/features/current");
      const map = {};
      (r.data.features || []).forEach((f) => {
        map[f.feature_key] = f;
      });
      setState({
        loading: false,
        error: null,
        package: r.data.package || null,
        features: map,
        enabledKeys: r.data.enabled_keys || [],
      });
    } catch (e) {
      // Fail-open: kalau gagal fetch, izinkan semua menu (jangan blokir admin).
      setState((s) => ({ ...s, loading: false, error: e?.message || "Gagal memuat feature flags" }));
    }
  }, []);

  // Re-fetch saat identity berubah (login/logout) — /features/current resolve
  // tenant dari authenticated user, jadi feature map harus mengikuti sesi baru.
  useEffect(() => {
    load();
  }, [load, user?.id]);

  return (
    <FeatureFlagContext.Provider value={{ ...state, refresh: load }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * useFeatureFlag(featureKey) — return { enabled, loading, source }
 *
 * Semantik v2 (FAIL-CLOSED):
 *   - Selama loading, `enabled` = false, `loading` = true.
 *   - Jika fetch GAGAL total (error) → fail-open (jangan kunci admin saat outage;
 *     backend gate 403 tetap pertahanan akhir).
 *   - Jika feature TIDAK ada di catalog (unknown key) → FAIL-CLOSED (disabled).
 */
export function useFeatureFlag(featureKey) {
  const { features, loading, error } = useContext(FeatureFlagContext);
  if (loading) return { enabled: false, loading: true, source: null, error };
  if (error) return { enabled: true, loading: false, source: "error-failopen", error };
  const f = features[featureKey];
  if (!f) {
    return { enabled: false, loading: false, source: null, error };
  }
  return { enabled: !!f.enabled, loading: false, source: f.source, error };
}

/**
 * useIsFeatureEnabled(featureKey, options?) — helper pendek untuk gating tegas.
 *
 * options.defaultEnabled  : nilai balik ketika loading. Default: false (fail-closed).
 * Saat fetch error → true (fail-open hanya untuk outage).
 * Unknown key setelah load sukses → false (fail-closed).
 */
export function useIsFeatureEnabled(featureKey, { defaultEnabled = false } = {}) {
  const { features, loading, error } = useContext(FeatureFlagContext);
  if (loading) return defaultEnabled;
  if (error) return true;
  const f = features[featureKey];
  if (!f) return false;
  return !!f.enabled;
}

export function useFeatureFlagsContext() {
  return useContext(FeatureFlagContext);
}
